import React, {useState, useEffect} from 'react'
import { 
  Form,
  FormGroup,
  Label,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  FormFeedback,
  Container,
  Row,
  Col,
} from 'reactstrap'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

import LoadingButton from './LoadingButton';
import { useMutation } from 'react-query';
import { updateStorageToken } from '../api/actions/profile';
import { fetcher } from '../api/client';
import { useProfile, useUpdateStorageToken } from '../api/hooks/profile';
import { useTranslation } from 'react-i18next';

function DropboxIntegration({ 
  showForm, 
  onClick, 
  isConnected,
  className,
  onReset,
  formik: { handleSubmit, handleChange, isSubmitting, values, errors, touched  }
}) {
  const { t } = useTranslation('common')

  if (isConnected) {
    return (
        <Row className={className}>
          <Col sm={6}>
            <FontAwesomeIcon icon={["fab","dropbox"]} className={`d-inline text-dropbox mr-2`} size="lg" />
            <strong>Dropbox</strong>
          </Col>
          <Col sm={6}>
            <FontAwesomeIcon icon="check-circle" className={`d-inline text-dropbox mr-2`} size="lg" style={{color: "green"}} />
            {t('connected')}
          </Col>
        </Row>
    )
  }

  if (showForm) {
    return (
      <Form>
        <FormGroup className={className}>
          <Label for={`code`}>{t('forms.dropboxCode')}<span className="text-accent">*</span></Label>
          <Button color="accent" size="sm" className="mb-2 float-right" onClick={onReset}>
            <FontAwesomeIcon icon="redo" className="mr-1"  onClick={onReset} size="sm" />
            {t('actions.reset')}
          </Button>
          <InputGroup>
            <Input
              type="text" 
              id={`code`}
              name={`code`}
              placeholder={t('forms.dropboxCode')}
              onChange={handleChange}  
              value={values.code}
              invalid={errors.code && touched.code}
            />
            <InputGroupAddon addonType="append">
              <LoadingButton loading={isSubmitting} className="form-control" color="primary" size="md" onClick={handleSubmit}>{t('actions.submit')}</LoadingButton>
            </InputGroupAddon>
            <FormFeedback>{t(`errors.${errors.code}`)}</FormFeedback>
          </InputGroup>
        </FormGroup>
      </Form>
    )
  }

  return (
    <div className={className}>
      <Button 
        color="dropbox" 
        size="md"
        onClick={onClick}
        tag="a"
        href="https://www.dropbox.com/oauth2/authorize?client_id=guuw1gnaww8rfit&response_type=code"
        target="_blank"
      > 
        <FontAwesomeIcon icon={["fab","dropbox"]} className={`d-inline text-light mr-2`} size="lg" />
        {t('actions.connectDropbox')}
      </Button>
    </div>
  )
}


const DropboxFormSchema = Yup.object().shape({
  code: Yup.string()
  .required('Required'),
});

function DropboxIntegrationContainer({onFinished = () => {}, className}) {
  const [showForm, setShowForm] = useState(false);
  const {profile} = useProfile();
  const isConnected = profile?.externalStorageConfig?.dropbox?.isConnected ?? false;
  
  const { mutate: mutateUpdateStorageToken, ...updateStorageTokenState } = useUpdateStorageToken({
    onSuccess: () => {
      setShowForm(false)
      onFinished({ succeed: true})
    },
    onError: (error) => {
      onFinished({ error: error, succeed: false })
    }
  });

  const formik = useFormik({
    initialValues: { code: '' },
    onSubmit: async (values, actions) => {
      await mutateUpdateStorageToken({
        type: 'dropbox',
        code: values.code
      }, {
        onError: () => {
          actions.setFieldError('code', 'WrongCode')
        }
      })

      actions.setSubmitting(false)
    },
    validationSchema: DropboxFormSchema
  })

  const handleShowForm = () => {
    setShowForm(true)
  }

  const handleReset = () => {
    setShowForm(false)
  }


  return <DropboxIntegration 
          className={className}
          showForm={showForm} 
          onClick={handleShowForm} 
          onReset={handleReset}
          isConnected={isConnected}
          formik={formik} 
        />
}


export default DropboxIntegrationContainer
